import { Divider, Stack, Typography, useTheme } from '@mui/material';
import { FileUploadMatchColumns } from 'features/co-pilot/co-pilot.types';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  columns: { text: string; type: string }[];
  fileName: string;
  hasTableHeading: boolean;
  handleColumnTypeChange(idx: number): (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleHasTableHeadingChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleResetFileColumnSelection(): void;
}

const LeadListColumnSelection: React.FC<Props> = ({
  columns,
  fileName,
  hasTableHeading,
  handleColumnTypeChange,
  handleHasTableHeadingChange,
  handleResetFileColumnSelection,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  const options: OptionType[] = [
    { value: FileUploadMatchColumns.EMPTY, label: t('doNotImport') },
    { value: FileUploadMatchColumns.FIRST_NAME, label: t('firstName') },
    { value: FileUploadMatchColumns.LAST_NAME, label: t('lastName') },
    { value: FileUploadMatchColumns.EMAIL, label: t('email') },
    { value: FileUploadMatchColumns.PHONE_NUMBER, label: t('phoneNumber') },
    { value: FileUploadMatchColumns.COMPANY, label: t('company') },
    { value: FileUploadMatchColumns.ROLE, label: t('position') },
  ];

  return (
    <Stack gap={1}>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Typography className={classes.greyText}>{`${t('fileName')}: ${fileName}`}</Typography>
        <PaiLink text={t('closeFile')} action={handleResetFileColumnSelection} size={'lg'} variant={'default'} underline={'always'} />
      </Stack>
      <Typography className={classes.blueText} mb={'8px'}>
        {t('fileColumnDialogContent')}
      </Typography>
      <Divider />
      <div style={{ margin: '8px 0' }}>
        <PaiCheckbox checked={hasTableHeading} onChange={handleHasTableHeadingChange} label={t('fileHasTableHeading')} />
      </div>
      <Divider />
      <div>
        <Stack direction={'row'} justifyContent={'space-between'} margin={'16px 0'}>
          <Typography className={classes.tableHead} sx={{ flex: 1 }}>
            {t('columnContentPreview')}
          </Typography>
          <Typography className={classes.tableHead} sx={{ flex: 1 }}>
            {t('columnLabel')}
          </Typography>
        </Stack>
        <Stack gap={1}>
          {columns.map((col, index) => (
            <Stack key={index} gap={1}>
              <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                <Typography className={classes.greyText} sx={{ flex: 1 }}>
                  {_.truncate(col.text, { length: 100 })}
                </Typography>
                <div style={{ flex: 1 }}>
                  <PaiSelect
                    value={col.type}
                    color={col.type === FileUploadMatchColumns.EMPTY ? theme.palette.greys.light : undefined}
                    onChange={handleColumnTypeChange(index)}
                    disabled={!col.text}
                  >
                    {options.map((option) => (
                      <PaiMenuItem
                        key={option.value}
                        value={option.value}
                        hidden={columns.some((c) => c.type === option.value && option.value !== '')}
                      >
                        {option.label}
                      </PaiMenuItem>
                    ))}
                  </PaiSelect>
                </div>
              </Stack>
              {index !== columns.length - 1 && <Divider />}
            </Stack>
          ))}
        </Stack>
      </div>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  blueText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  tableHead: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
}));

export default LeadListColumnSelection;
